import { useTranslation } from "react-i18next";
import { Store, Truck, Crosshair, Undo2, Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

export const BranchSelect = ({ groups, value, onChange, testId }) => {
  const { t } = useTranslation();
  return (
    <select className="field-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {groups.map((g) => (
        <optgroup key={g.emirate} label={t(g.emirate)}>
          {g.stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
};

// Approx emirate centroids — used to snap a geolocation to the nearest delivery emirate.
const CENTROIDS = {
  dubai: [25.20, 55.27], sharjah: [25.35, 55.40], ajman: [25.41, 55.44],
  umm_al_quwain: [25.56, 55.55], ras_al_khaimah: [25.79, 55.95], fujairah: [25.13, 56.34],
};
const nearestEmirate = (lat, lng, keys) => {
  let best = null, bestD = Infinity;
  keys.forEach((k) => {
    const c = CENTROIDS[k];
    if (!c) return;
    const d = (lat - c[0]) ** 2 + (lng - c[1]) ** 2;
    if (d < bestD) { bestD = d; best = k; }
  });
  return best;
};

const DeliveryChargesInfo = ({ testId }) => {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={t("Delivery and collection charges")} title={t("Delivery and collection charges")}
          className="text-[#009900] hover:text-[#144733] transition-colors" data-testid={testId}>
          <Info className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 text-xs" data-testid={`${testId}-popover`}>
        <p className="font-semibold text-[#144733] mb-2">{t("Delivery and collection charges")}</p>
        <p className="text-[#57685e] mb-2">{t("A small charge may apply depending on your emirate. We always confirm the exact amount before you pay.")}</p>
        <ul className="space-y-1 text-[#41504a]">
          <li className="flex justify-between gap-2"><span>{t("Dubai, Sharjah, Ajman")}</span><span className="font-semibold whitespace-nowrap">AED 100 + VAT</span></li>
          <li className="flex justify-between gap-2"><span>{t("Ras Al Khaimah, Umm Al Quwain, Fujairah")}</span><span className="font-semibold whitespace-nowrap">AED 200 + VAT</span></li>
        </ul>
        <p className="text-[#009900] font-medium mt-2">{t("Monthly rentals enjoy free delivery across Dubai and Sharjah.")}</p>
      </PopoverContent>
    </Popover>
  );
};

const DeliveryEmiratePicker = ({ emirates, value, useCurrent, onEmirate, onUseCurrent, label, testPrefix, note }) => {
  const { t } = useTranslation();
  const detect = () => {
    if (!navigator.geolocation) { onUseCurrent(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const k = nearestEmirate(pos.coords.latitude, pos.coords.longitude, emirates.map((e) => e.key));
        if (k) { onEmirate(k); onUseCurrent(true); }
      },
      () => onUseCurrent(false),
      { timeout: 8000, enableHighAccuracy: false }
    );
  };
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <label className="field-label mb-0">{label}</label>
        <DeliveryChargesInfo testId={`${testPrefix}-charges-info-btn`} />
      </div>
      <div className="flex gap-2">
        <select className="field-input" value={value} onChange={(e) => { onEmirate(e.target.value); onUseCurrent(false); }} data-testid={`${testPrefix}-emirate-select`}>
          {emirates.map((e) => <option key={e.key} value={e.key}>{t(e.label)}</option>)}
        </select>
        <button type="button" onClick={detect} title={t("Find my location")} aria-label={t("Find my location")}
          className={`shrink-0 w-11 rounded-lg border-2 flex items-center justify-center transition-colors ${useCurrent ? "border-[#009900] bg-[#f0f9f0] text-[#009900]" : "border-[#d5ddd5] text-[#57685e] hover:border-[#009900]"}`}
          data-testid={`${testPrefix}-current-location-btn`}>
          <Crosshair className="w-4 h-4" />
        </button>
      </div>
      {note && (
        <p className="text-[11px] text-[#009900] mt-1.5 font-medium" data-testid={`${testPrefix}-eta`}>
          {useCurrent ? t("Using your current location, we'll confirm your exact area. ") : ""}{note}
        </p>
      )}
    </div>
  );
};

const OptionToggle = ({ active, onClick, icon: Icon, label, testId }) => (
  <button type="button" onClick={onClick}
    className={`rounded-lg border-2 px-3 py-3 text-left transition-colors ${active ? "border-[#009900] bg-[#f0f9f0]" : "border-[#d5ddd5] bg-white hover:border-[#9db8a5]"}`}
    data-testid={testId}>
    <Icon className={`w-4 h-4 mb-1 ${active ? "text-[#009900]" : "text-[#57685e]"}`} />
    <div className="text-xs font-semibold uppercase tracking-wide text-[#144733]">{label}</div>
  </button>
);

export const HandoverPicker = ({ branches, value, onChange, mode }) => {
  const { t } = useTranslation();
  const set = (patch) => onChange({ ...value, ...patch });
  const emirates = branches.delivery_emirates;
  const emLabel = (key) => t((emirates.find((e) => e.key === key) || {}).label || key);
  const deliverNote = t("Fast doorstep delivery, to you in as little as 3 hours.");
  const returnGroups = branches.dropoff_extra?.length
    ? [...branches.groups, { emirate: "Abu Dhabi", stations: branches.dropoff_extra }]
    : branches.groups;
  const DXBSHJ_AIRPORTS = ["DXB-T1", "DXB-T2", "DXB-T3", "SHJ-AP"];
  const stationById = {};
  returnGroups.forEach((g) => g.stations.forEach((s) => { stationById[s.id] = s; }));
  const computeHandoverFee = () => {
    if (value.type !== "branch" || value.returnType !== "branch") return null;
    const p = stationById[value.branchId], d = stationById[value.returnBranchId];
    if (!p || !d) return null;
    const parts = []; let total = 0;
    if (d.id === "AUH-AP") {
      parts.push(t("AED 275 + VAT one-way drop-off at Abu Dhabi Airport")); total += 288.75;
      if (DXBSHJ_AIRPORTS.includes(p.id)) { parts.push(t("plus AED 50 + VAT airport fee")); total += 52.5; }
    } else {
      const pAir = DXBSHJ_AIRPORTS.includes(p.id), dAir = DXBSHJ_AIRPORTS.includes(d.id);
      if (pAir || dAir) { parts.push(pAir && dAir ? t("AED 50 + VAT airport fee, charged once (the second airport is on us)") : t("AED 50 + VAT airport fee")); total += 52.5; }
      if (!dAir && p.region !== d.region) { parts.push(t("AED 50 + VAT one-way drop-off fee")); total += 52.5; }
    }
    return { parts, total };
  };
  const handoverFee = computeHandoverFee();

  const chooseBranchPickup = () => set({ type: "branch", returnType: "branch", returnBranchId: value.returnBranchId || value.branchId });
  const chooseDeliverToMe = () => set({ type: "delivery", returnType: "collect", returnEmirate: value.emirate });
  const changeDeliveryEmirate = (k) => set({ emirate: k, returnEmirate: value.returnType === "collect" ? k : value.returnEmirate });

  return (
    <div className="grid md:grid-cols-2 gap-5">
      {/* PICKUP */}
      <div>
        <label className="field-label">{mode === "monthly" ? t("Start / pickup option *") : t("Pickup option *")}</label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <OptionToggle active={value.type === "branch"} onClick={chooseBranchPickup} icon={Store} label={t("Branch pickup")} testId="handover-branch-btn" />
          <OptionToggle active={value.type === "delivery"} onClick={chooseDeliverToMe} icon={Truck} label={t("Deliver to me")} testId="handover-delivery-btn" />
        </div>
        {value.type === "branch" ? (
          <>
            <label className="field-label">{t("Pickup branch *")}</label>
            <BranchSelect groups={branches.groups} value={value.branchId} onChange={(v) => set({ branchId: v })} testId="pickup-branch-select" />
          </>
        ) : (
          <DeliveryEmiratePicker emirates={emirates} value={value.emirate} useCurrent={value.useCurrentLocation}
            onEmirate={changeDeliveryEmirate} onUseCurrent={(b) => set({ useCurrentLocation: b })}
            label={t("Emirate of delivery *")} testPrefix="delivery" note={deliverNote} />
        )}
      </div>

      {/* RETURN */}
      <div>
        <label className="field-label">{t("Return option *")}</label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <OptionToggle active={value.returnType === "branch"} onClick={() => set({ returnType: "branch", returnBranchId: value.returnBranchId || value.branchId })} icon={Store} label={t("Branch return")} testId="return-branch-btn" />
          <OptionToggle active={value.returnType === "collect"} onClick={() => set({ returnType: "collect", returnEmirate: value.returnEmirate || value.emirate })} icon={Truck} label={t("Collect from my location")} testId="return-collect-btn" />
        </div>
        {value.returnType === "branch" ? (
          <>
            <label className="field-label">{t("Return branch *")}</label>
            <BranchSelect groups={returnGroups} value={value.returnBranchId} onChange={(v) => set({ returnBranchId: v })} testId="return-branch-select" />
            {handoverFee && handoverFee.parts.length > 0 ? (
              <div className="mt-1.5 rounded-lg border border-[#FFDC00] bg-[#FFF9DC]/60 px-3 py-2 text-[11px] text-[#144733]" data-testid="oneway-fee-note">
                <strong>{t("Heads up:")}</strong> {handoverFee.parts.join(" · ")}. {t("About AED {{amt}} incl. VAT, added automatically and shown in your price before you pay.", { amt: handoverFee.total.toFixed(2) })}
              </div>
            ) : (
              <p className="mt-1.5 text-[11px] text-[#009900]" data-testid="oneway-fee-note">{t("No one-way fee for this return.")}</p>
            )}
          </>
        ) : value.type === "delivery" ? (
          <div className="bg-[#f0f9f0] border border-[#009900]/20 rounded-lg px-3 py-2.5 text-xs text-[#144733] flex items-start gap-2" data-testid="return-same-location-note">
            <Undo2 className="w-4 h-4 text-[#009900] shrink-0 mt-0.5" />
            <span>{t("We'll collect it from your delivery location in {{place}}.", { place: emLabel(value.emirate) })}</span>
          </div>
        ) : (
          <DeliveryEmiratePicker emirates={emirates} value={value.returnEmirate || value.emirate} useCurrent={value.returnUseCurrentLocation}
            onEmirate={(k) => set({ returnEmirate: k })} onUseCurrent={(b) => set({ returnUseCurrentLocation: b })}
            label={t("Emirate for collection *")} testPrefix="return-collect" note={t("We'll collect the car from wherever suits you.")} />
        )}
      </div>
    </div>
  );
};
